import { For, type JSX, Show, splitProps } from 'solid-js'
import './Skeleton.css'

export type SkeletonVariant =
  | 'text'
  | 'avatar'
  | 'image'
  | 'card'
  | 'list'
  | 'table'
  | 'circular'
  | 'rectangular'
export type SkeletonAnimation = 'shimmer' | 'pulse' | 'none'
export interface SkeletonProps extends JSX.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant
  width?: string | number
  height?: string | number
  lines?: number
  rows?: number
  columns?: number
  animation?: SkeletonAnimation
  density?: 'comfortable' | 'compact'
  label?: string
}

const count = (value: number | undefined, fallback: number, max: number) =>
  Math.min(
    max,
    Math.max(1, Math.floor(typeof value === 'number' && Number.isFinite(value) ? value : fallback))
  )
const length = (value: string | number | undefined) =>
  typeof value === 'number' ? `${value}px` : value

function TextLines(props: { count: number; height?: string | undefined }) {
  return (
    <div class="ds-skeleton-lines">
      <For each={Array.from({ length: props.count })}>
        {(_, index) => (
          <span
            class="ds-skeleton-block ds-skeleton-line"
            style={{
              height: props.height,
              width: props.count > 1 && index() === props.count - 1 ? '64%' : '100%',
            }}
          />
        )}
      </For>
    </div>
  )
}

export function Skeleton(props: SkeletonProps) {
  const [local, rest] = splitProps(props, [
    'variant',
    'width',
    'height',
    'lines',
    'rows',
    'columns',
    'animation',
    'density',
    'label',
    'style',
    'class',
    'children',
  ])
  const variant = () => local.variant ?? 'text'
  const lineCount = () => count(local.lines, 3, 20)
  const rowCount = () => count(local.rows, 3, 12)
  const columnCount = () => count(local.columns, 4, 8)
  const dimensionStyle = () =>
    [
      local.width === undefined ? '' : `width:${length(local.width)};`,
      local.height === undefined ? '' : `height:${length(local.height)};`,
    ].join('')
  return (
    <div
      {...rest}
      role={local.label ? 'status' : undefined}
      aria-label={local.label}
      aria-hidden={local.label ? undefined : true}
      aria-busy={local.label ? true : undefined}
      data-variant={variant()}
      class={`ds-skeleton ds-skeleton--${variant()} ds-skeleton--${local.animation ?? 'shimmer'} ${local.density === 'compact' ? 'ds-skeleton--compact' : ''} ${local.class ?? ''}`}
      style={
        typeof local.style === 'string'
          ? `${dimensionStyle()}${local.style}`
          : {
              ...local.style,
              ...(local.width === undefined ? {} : { width: length(local.width) }),
              ...(local.height === undefined ? {} : { height: length(local.height) }),
            }
      }
    >
      <Show when={variant() === 'text'}>
        <TextLines
          count={count(local.lines, 1, 20)}
          height={local.lines === undefined || local.lines === 1 ? length(local.height) : undefined}
        />
      </Show>
      <Show when={['avatar', 'circular', 'image', 'rectangular'].includes(variant())}>
        <span
          class={`ds-skeleton-block ds-skeleton-shape ${variant() === 'circular' ? 'rounded-full' : ''}`}
        />
      </Show>
      <Show when={variant() === 'card'}>
        <div class="ds-skeleton-card-media ds-skeleton-block" />
        <div class="ds-skeleton-card-body">
          <span class="ds-skeleton-block ds-skeleton-title" />
          <TextLines count={lineCount()} />
          <div class="ds-skeleton-card-footer">
            <span class="ds-skeleton-block ds-skeleton-avatar" />
            <span class="ds-skeleton-block ds-skeleton-caption" />
            <span class="ds-skeleton-block ds-skeleton-action" />
          </div>
        </div>
      </Show>
      <Show when={variant() === 'list'}>
        <For each={Array.from({ length: rowCount() })}>
          {() => (
            <div class="ds-skeleton-row">
              <span class="ds-skeleton-block ds-skeleton-avatar" />
              <TextLines count={count(local.lines, 2, 4)} />
              <span class="ds-skeleton-block ds-skeleton-action" />
            </div>
          )}
        </For>
      </Show>
      <Show when={variant() === 'table'}>
        <div class="ds-skeleton-table" style={{ '--skeleton-columns': columnCount() }}>
          <For each={Array.from({ length: rowCount() + 1 })}>
            {(_, row) => (
              <div class="ds-skeleton-table-row" data-header={row() === 0 ? '' : undefined}>
                <For each={Array.from({ length: columnCount() })}>
                  {(_, column) => (
                    <span
                      class="ds-skeleton-block ds-skeleton-line"
                      style={{ width: `${[72, 54, 64, 48][column() % 4]}%` }}
                    />
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}
